import { DependencyList, useEffect, useState } from 'react';
import Sensor, { ResizeSensorCallback } from 'css-element-queries/src/ResizeSensor';
import { Nullable } from '@e2m-customer-portal/shared';

interface Size {
  /**
   * Width in pixels.
   */
  width: number;

  /**
   * Height in pixels.
   */
  height: number;
}

/**
 * @summary Hook to sense when the dimensions of an element change.
 *
 * Hook to sense when the dimensions of an element change. That change may be triggered by window resizing, animations
 * or DOM changes.
 *
 * When a callback is given, the function returns nothing and calls the callback function if a size change is detected.
 *
 * @param {Element | Element[] | null | undefined} element
 * @param {ResizeSensorCallback} callback
 * @param {DependencyList} [deps]
 *
 * @example
 * useResizeSensor(elementRef, () => {
 *   // do something if the element changed size
 * });
 */
export function useResizeSensor(
  element: Nullable<Element | Element[]>,
  callback: ResizeSensorCallback,
  deps?: DependencyList,
): void;

/**
 * @summary Hook to sense when the dimensions of an element change.
 *
 * Hook to sense when the dimensions of an element change. That change may be triggered by window resizing, animations
 * or DOM changes.
 *
 * When no callback is given, the function returns the size of the element. It refreshes every time a size change is
 * detected.
 *
 * @param {Element | Element[] | null | undefined} element
 * @param {DependencyList} [deps]
 * @returns {Size | undefined}
 *
 * @example
 * const size = useResizeSensor(elementRef);
 */
export function useResizeSensor(element: Nullable<Element | Element[]>, deps?: DependencyList): Size | undefined;

export function useResizeSensor(
  element: Nullable<Element | Element[]>,
  callbackOrDeps?: ResizeSensorCallback | DependencyList,
  maybeDeps?: DependencyList,
): Size | undefined {
  const callback = typeof callbackOrDeps === 'function' ? callbackOrDeps : undefined;
  const deps = Array.isArray(callbackOrDeps) ? callbackOrDeps : maybeDeps;

  const [state, setState] = useState<Size>();

  useEffect(
    () => {
      if (element == null || (Array.isArray(element) && element.length === 0)) {
        return undefined;
      }

      const sensor = new Sensor(element, (size) => {
        if (callback) {
          callback(size);
        } else {
          setState(size);
        }
      });

      return () => sensor.detach();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    deps ?? [element, callback],
  );

  return callback ? undefined : state;
}
